import { useState, useEffect } from 'react';
import { MessageCircle, ChevronRight, Radio, HelpCircle, Crosshair, BatteryLow, Compass } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';

interface CoachCTAProps {
  hasEngagedToday: boolean;
  userName?: string;
  lastTopic?: string;
}

interface WeeklyCommitment {
  values: string[];
  zeroExcuse: string;
  mastery: string;
  ownMetric: string;
}

const DEFAULT_PROMPTS = [
  { text: "Help me focus", icon: Crosshair },
  { text: "I'm stuck", icon: HelpCircle },
  { text: "Need energy", icon: BatteryLow }
];

const CONTEXTUAL_MESSAGES = [
  "Let's make today count",
  "Ready when you are",
  "What's on your mind?",
  "Let's work through it together"
];

export function CoachCTA({ hasEngagedToday, userName, lastTopic }: CoachCTAProps) {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [pulseVisible, setPulseVisible] = useState(true);
  const [weeklyCommitment, setWeeklyCommitment] = useState<WeeklyCommitment | null>(null);

  useEffect(() => {
    setMounted(true);
    loadWeeklyCommitment();
    const interval = setInterval(() => {
      setPulseVisible(v => !v);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  async function loadWeeklyCommitment() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('weekly_plan_values, weekly_plan_zero_excuse, weekly_plan_mastery, weekly_plan_own_metric, weekly_plan_locked_at')
        .eq('id', user.id)
        .maybeSingle();

      if (profile?.weekly_plan_locked_at) {
        const lockedDate = new Date(profile.weekly_plan_locked_at);
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());

        if (lockedDate >= weekStart) {
          setWeeklyCommitment({
            values: profile.weekly_plan_values || [],
            zeroExcuse: profile.weekly_plan_zero_excuse || '',
            mastery: profile.weekly_plan_mastery || '',
            ownMetric: profile.weekly_plan_own_metric || ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading weekly commitment:', error);
    }
  }

  const randomMessage = CONTEXTUAL_MESSAGES[Math.floor(Date.now() / 86400000) % CONTEXTUAL_MESSAGES.length];

  const getContextualPrompts = () => {
    if (weeklyCommitment) {
      const prompts = [];

      if (weeklyCommitment.zeroExcuse) {
        prompts.push({
          text: `Overcome "${weeklyCommitment.zeroExcuse}"`,
          icon: Compass
        });
      }

      if (weeklyCommitment.mastery) {
        prompts.push({
          text: `Practice ${weeklyCommitment.mastery}`,
          icon: Crosshair
        });
      }

      prompts.push({ text: "I'm stuck", icon: HelpCircle });

      return prompts.slice(0, 3);
    }

    return DEFAULT_PROMPTS;
  };

  const quickPrompts = getContextualPrompts();

  const getCoachMessage = () => {
    if (hasEngagedToday && lastTopic) {
      return `Continue talking about ${lastTopic}`;
    }

    if (weeklyCommitment && weeklyCommitment.values.length > 0) {
      const topValue = weeklyCommitment.values[0];
      return `Let's work on ${topValue.toLowerCase()} today`;
    }

    return userName ? `${userName}, ${randomMessage.toLowerCase()}` : randomMessage;
  };

  return (
    <div className={`relative overflow-hidden rounded-2xl transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />

      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-orange-500/15 rounded-full blur-3xl translate-y-1/2" />
      </div>

      <div className="absolute inset-0 backdrop-blur-3xl" />

      <div className="relative p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="relative">
            <div className={`absolute inset-0 bg-amber-400/30 rounded-2xl blur-lg transition-opacity duration-1000 ${pulseVisible ? 'opacity-100' : 'opacity-40'}`} />
            <div className="relative w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
              <MessageCircle className="h-7 w-7 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-gray-900 animate-pulse" />
          </div>

          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-center gap-2 mb-1.5">
              <h3 className="font-bold text-xl text-white">Your Coach</h3>
              {hasEngagedToday && (
                <span className="flex items-center gap-1.5 text-xs bg-white/10 backdrop-blur-sm text-amber-300 px-2.5 py-1 rounded-full border border-white/10">
                  <Radio className="h-3 w-3" />
                  Active
                </span>
              )}
            </div>
            <p className="text-gray-400 text-sm">
              {getCoachMessage()}
            </p>
            {weeklyCommitment && weeklyCommitment.values.length > 0 && !hasEngagedToday && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {weeklyCommitment.values.slice(0, 3).map((value) => (
                  <span key={value} className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded border border-amber-500/30">
                    {value}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 mb-5 overflow-x-auto pb-1 -mx-1 px-1">
          {quickPrompts.map((prompt, index) => {
            const Icon = prompt.icon;
            return (
              <button
                key={prompt.text}
                onClick={() => navigate('/coach', { state: { prompt: prompt.text } })}
                className={`flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl text-sm font-medium text-white/90 hover:text-white transition-all duration-300 whitespace-nowrap ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                style={{ transitionDelay: `${200 + index * 75}ms` }}
              >
                <Icon className="h-4 w-4 text-amber-400" />
                {prompt.text}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => navigate('/coach')}
          className={`w-full relative overflow-hidden flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-gray-900 font-bold rounded-xl transition-all duration-300 group shadow-lg shadow-amber-500/20 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
          style={{ transitionDelay: '400ms' }}
        >
          <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          <span className="relative">
            {hasEngagedToday ? 'Continue session' : 'Start coaching'}
          </span>
          <ChevronRight className="h-5 w-5 relative group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      </div>
    </div>
  );
}
